import { DialogData } from './dialog.model';
import { EAccountReportStatus } from './account.model';

export const BILLING_STATUS_ACTIVE = 'Active';
export const BILLING_STATUS_EXPIRED = 'Expired';
export const BILLING_STATUS_PAYMENT_FAILED = 'Payment failed';
export const BILLING_STATUS_MISSING_REGISTRATION = 'Missing registration';

export const BILLING_STATUSES = [
  BILLING_STATUS_ACTIVE,
  BILLING_STATUS_EXPIRED,
  BILLING_STATUS_PAYMENT_FAILED,
  BILLING_STATUS_MISSING_REGISTRATION,
] as const;

export interface User {
  name: string;
  id: number;
}

export interface AccessToken {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: 'string';
  user_id: number;
}

export enum UserType {
  SINGLE_USER = 'user',
  MANAGER = 'manager',
  MASTER = 'master',
  VSM = 'vsm',
  PARTNER = 'partner',
}

export interface IUserRedirect {
  route: string;
  params: {
    [key: string]: any;
  };
}

export interface IUserInvoice {
  id: number;
  charge_date: string;
}

export interface IUserPermissions {
  user_vip: boolean;
  can_find_template: boolean;
  can_assign: boolean;
  can_update_account: boolean;
  can_update_campaign: boolean;
}

export interface IUserGoalPreferences {
  prefered_appointment_goal: string;
  prefered_average_cycle: string;
  prefered_average_sale: number;
  prefered_goals_monthly: number;
  prefered_goals_weekly: number;
  prefered_sales_goal: string;
}

export interface IUserPersonalInfo {
  first_name: string;
  last_name: string;
  company: string;
  inbox_refresh_time: number;
}

export enum EInboxType {
  CYCLE = 'cycle',
  CAMPAIGN = 'campaign',
}

export interface IInboxManagementSettings {
  enabled: boolean;
  inbox_campaign: number;
  inbox_delay: number;
  inbox_group: number;
  inbox_type: EInboxType; // change to enum
  meeting_campaign: number;
  meeting_delay: number;
  meeting_group: number;
  on: boolean;
}

export interface IIntegration {
  id: number;
  map: boolean;
  name: string;
}

export interface IUserSessionData
  extends IUserGoalPreferences,
    IUserPersonalInfo {
  account_status: 'ok' | 'danger' | 'warning';
  id: number;
  user_type: UserType;
  email: string;
  message_per_page: number;
  signature: string;
  time_zone: string;
  address1: string;
  address2: string;
  bananced_credits: number;
  permissions: IUserPermissions;
  c2recruit: boolean;
  one_time_message: boolean;
  can_switch: boolean;
  city: string;
  guide_progress: number;
  last_sign_in_at: string;
  last_sign_in_ip: string;
  partner_id: number;
  manager_id: number;
  phone_number: string;
  skip_guide: boolean;
  state: string;
  zip: string;
  user_policy_accepted: boolean;
  transaction_id: string;
  emails_preview: boolean;
  sales_force_connected: boolean;
  roles: any[];
  invoices: IUserInvoice[];
  unsubscribe: boolean;
  confirmed: boolean;
  popup_tips: boolean;
  vip_mode: boolean;
  vsm: boolean;
  created_by_id: string;
  vsm_id: number;
  vsm_comment: number;
  delay_job_started: boolean;
  sent_today: number;
  emails_perday: number;
  default_refresh_time: number;
  dst: boolean;
  export_contact: boolean;

  send_limit: number;
  inbox_management_settings?: IInboxManagementSettings;
  vsm_group_id?: number;
  country?: string;
  billing_status: typeof BILLING_STATUSES[number];

  campaigns_suspended?: boolean;
  emails_suspended?: boolean;
  send_sms?: boolean;
  mobile_phone?: string;
  integration: IIntegration;
  reports: EAccountReportStatus;
  cos?: string;
  service_flag?: string;
  franchis_number?: number;
  invoiced: boolean;
  account_note: string;
  invoice_note: string;
  optional_note_1: string;
  optional_note_2: string;
  check_duplicate: boolean;
  check_user_email_domain?: boolean;
  plan_id?: number;
  /** True if a team/discount (coupon) is already on the local subscription record. */
  has_payment_discount?: boolean;
  /** Publishable key for Stripe.js (Payment Element). */
  stripe_publishable_key?: string;
  /** Default payment method summary from Stripe (type, last4, brand, etc.). */
  payment_method?: {
    type?: string;
    brand?: string;
    last4?: string;
    bank_name?: string;
    account_type?: string;
  };
}

export interface UserState {
  user: User;
  loggedIn: boolean;
  redirectRequired: boolean;
  redirectTo?: IUserRedirect;
  sessionData: IUserSessionData;
  flashMessages?: IFlash[];
  status?: string;
  error?: any;
  vsmLockMessageId?: number;
  updateInProgress?: boolean;
}

export enum FLASH_TYPE {
  DIALOG,
  ALERT,
}

export interface IFlash {
  type: FLASH_TYPE;
  dialogData?: DialogData;
  alertMessage?: string;
}

export enum UserActions {
  LOGIN_INITIALIZE = '[User] Initialize',
  LOGIN_INITIALIZE_SUCCESS = '[User] Initialize Success',
  LOGIN_ACTION = '[User] Login',
  LOGIN_ACTION_SUCCESS = '[User] Login Success',
  LOGIN_ACTION_FAILURE = '[User] Login Failed',
  LOGOUT = '[User] Logout',
  LOGOUT_SUCCESS = '[User] Logout success',
  LOGIN_REDIRECT_BILLING = '[User] Login Redirect Billing',
  LOGIN_REDIRECT_CONFIRMATION = '[User] Login Redirect Confirmation',
  GET_USER_SESSION = '[User] Get User Session',
  SET_USER_SESSION_DATA = '[User] Set User Session Data',
  USER_SESSION_DATA_ERROR = '[User] User Session Data Error',
  SET_FLASH = '[User] Set Flash',
  POP_FLASH = '[User] Pop Flash',
  TOGGLE_EMAIL_PREVIEW = '[User] Toggle email preview',
  TOGGLE_EMAIL_PREVIEW_SUCCESS = '[User] Toggle email preview Success',
  TOGGLE_VIP_FEATURE = '[User] Toggle VIP Feature',
  TOGGLE_VIP_FEATURE_SUCCESS = '[User] Togle VIP Feature Success',
  TOGGLE_EMAIL_PREVIEW_ERROR = '[User] Toggle email preview Error',
  TOGGLE_HINTS = '[User] Toggle Hints',
  TOGGLE_HINTS_SUCCESS = '[User] Toggle Hints Success',
  TOGGLE_UNSUBSCRIBE = '[User] Toggle Unsubscribe',
  TOGGLE_UNSUBSCRIBE_SUCCESS = '[User] Toggle Unsubscribe Success',
  SET_SIGNATURE = '[User] Set Signature',
  UPDATE_TIMEZONE = '[User] Update Timezone',
  VSM_MESSAGE_LOCK = '[User] VSM message lock',
  SET_SMS_DETAILS = '[User] Set SMS Details'
}

export interface IIntegrationMap {
  [key: string]: any;
}

export interface IIntegrationFields {
  [key: string]: {
    control: string;
    control_source: string;
    label: string;
    required: boolean;
    type: string; // TODO: create predefined types
  };
}

export interface IIntegrationData {
  extra_data: any;
  map: IIntegrationMap;
  fields: IIntegrationFields;
}

export interface IUserBillingInfo {
  paid_by?: string;
  card_number?: number;
  card_expiration_date?: string;
  country?: string;
  name_on_card?: string;
  cvc_code?: string;
  state?: string;
  /** From GET /v1/users/{id} — present when a team/discount (coupon) is on file */
  team_code?: number | null;
  team_code_name?: string;
}